'use client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocale } from 'use-intl';
import { useMedia, handleLogoutStay } from '@ssv-website/hooks';
import { MessagePlugin } from 'tdesign-react';
import BannerSection from './_bs/BannerSection';
import HighlightsSection from './_bs/HighlightsSection';
import GuestsSection from './_bs/GuestsSection';
import AgendaSection from './_bs/AgendaSection';
import AiForGoodSection from './_bs/AiForGoodSection';
import ExhibitionSection from './_bs/ExhibitionSection';
import VideosSection from './_bs/VideosSection';
import MomentsSection from './_bs/MomentsSection';
import NewsSection from './_bs/NewsSection';
import ReportsSection from './_bs/ReportsSection';
import PartnersSection from './_bs/PartnersSection';
import PublicBanner from './_bs/PublicBanner';
import type { PublicBannerTabId } from './_bs/PublicBanner';
import SSVFooter from '../components/SSVFooter/index';
import { getCosImageUrl } from '../../cos-image';
import { PCNavbar } from '../../navbar';
import SSVNavbar from '../components/SSVNavbar';
import ConferenceChatFloatingEntry from './ConferenceChatFloatingEntry';
import { MeetingConfigProvider } from '../s-2025/context/MeetingConfigContext';
import { getLanguageSwitchMeta, switchLanguage } from '../i18n/config';
import { getTitle } from './config';
import styles from './Conference.module.css';
import ConferenceMobileRemController from './ConferenceMobileRemController';
import {
  clearConferenceChatPendingAction,
  loadConferenceChatPendingAction,
  requestConferenceChatEntry,
} from '../../navbar/conferenceChat';
import {
  clearConferenceSignupPendingAction,
  fetchConferenceBrands,
  fetchConferenceMoments,
  loadConferenceSignupPendingAction,
  normalizeConferenceMoments,
  requestConferenceSignupRedirect,
} from './api';
import {
  isConferenceSectionYearStateVisible,
} from './sectionYearState';
import type {
  ConferenceAiForGoodItem,
  ConferenceAiForGoodListResp,
  ConferenceBrandItem,
  ConferenceDynamicData,
  ConferenceExhibitionListResp,
  ConferenceHighlightItem,
  ConferenceMomentListResp,
  ConferenceNewsListResp,
  ConferenceSite,
  ConferenceThemeItem,
} from './types';
import { normalizeConferenceNewsData } from './news.helpers';
import {
  fetchConferenceInitialSnapshot,
  isSameConferenceData,
  mergeConferenceInitialSnapshot,
  type ConferenceInitialSnapshot,
  type ConferenceIndependentSectionStates,
} from './initialSnapshot';

const hasConferenceAiForGoodItemData = (item?: ConferenceAiForGoodItem) =>
  [
    item?.productNameCN,
    item?.productNameEN,
    item?.productDescCN,
    item?.productDescEN,
    item?.logo,
    item?.pcLinkURL,
    item?.mobileLinkURL,
    item?.wxLinkURL,
    item?.link,
  ].some((value) => `${value || ''}`.trim());

const getConferenceAiForGoodList = (data?: ConferenceAiForGoodListResp) =>
  (data?.list || []).filter(hasConferenceAiForGoodItemData);

const createEmptyAiForGoodResp = (): ConferenceAiForGoodListResp => ({
  list: [],
  total: 0,
});

const createEmptyConferenceExhibitionResp =
  (): ConferenceExhibitionListResp => ({
    list: [],
    total: 0,
  });

const createEmptyConferenceNewsResp = (): ConferenceNewsListResp =>
  normalizeConferenceNewsData();

// Figma Frame Right (7011:20453)：单胶囊，1px 白色描边 32 圆角，
// 14×14 地球 icon + 12px/500「EN」文字。点击行为与 s-2026-signup
// 的 LanguageSwitchButton 一致 —— 直接走 switchLanguage 切域名。
function LanguageGlobeIcon({
  size = 14,
  color = '#FFFFFF',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
    >
      <circle cx="7" cy="7" r="5.5" stroke={color} strokeWidth="1" />
      <path
        d="M1.5 7h11M7 1.5c1.5 1.4 2.3 3.2 2.3 5.5S8.5 11.1 7 12.5M7 1.5C5.5 2.9 4.7 4.7 4.7 7s.8 4.1 2.3 5.5"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ConferenceLanguageSwitch() {
  const locale = useLocale();
  const currentLocale = locale === 'en' ? 'en' : 'zh';
  const { targetLang, langLabel } = getLanguageSwitchMeta(currentLocale);
  const displayLangLabel = targetLang === 'zh' ? '中文' : langLabel;
  const handleClick = () => {
    switchLanguage(targetLang);
  };
  const ariaLabel = targetLang === 'en' ? 'Switch to English' : '切换到中文';
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      className="inline-flex h-[25px] cursor-pointer items-center justify-center gap-1 rounded-[32px] border border-white px-3"
    >
      <span className="inline-flex size-[14px] shrink-0 items-center justify-center leading-none">
        <LanguageGlobeIcon size={14} color="#FFFFFF" />
      </span>
      <span className="inline-flex h-[14px] items-center text-xs font-medium leading-[14px] text-white">
        {displayLangLabel}
      </span>
    </button>
  );
}

interface SSVMeeting2026Props {
  data?: any;
  isExternalDomain?: boolean;
  site?: ConferenceSite;
  initialSnapshot?: ConferenceInitialSnapshot;
}

const hasConferenceListData = <T,>(list?: T[]) =>
  Array.isArray(list) && list.length > 0;

const normalizeBrandContent = (list?: ConferenceBrandItem[]) =>
  (Array.isArray(list) ? list : []).filter(
    (item) => item.contentUrl && item.status !== 0
  );

const hasSameBrandContent = (
  current: ConferenceBrandItem[],
  next: ConferenceBrandItem[]
) => JSON.stringify(current) === JSON.stringify(next);

export default function SSVMeeting2026({
  data,
  isExternalDomain = false,
  site,
  initialSnapshot,
}: SSVMeeting2026Props) {
  const locale = useLocale();
  const isMobile = useMedia('mobile');
  const mainConfig = data?.meetingMainConfig?.[0] || {};
  const [selectedYear, setSelectedYear] = useState(
    initialSnapshot?.selectedYear || new Date().getFullYear()
  );
  const [yearOptions, setYearOptions] = useState<number[]>(
    initialSnapshot?.yearOptions || []
  );
  const [independentSectionsReady, setIndependentSectionsReady] =
    useState(initialSnapshot?.independentSectionsReady ?? false);
  const [independentSectionStates, setIndependentSectionStates] =
    useState<ConferenceIndependentSectionStates>(
      initialSnapshot?.independentSectionStates || {
        guests: { availableYears: [] },
        videos: { availableYears: [] },
        agenda: { availableYears: [] },
        aiForGood: { availableYears: [] },
        exhibition: { availableYears: [] },
        moments: { availableYears: [] },
        news: { availableYears: [] },
      }
    );
  const [dynamicData, setDynamicData] = useState<ConferenceDynamicData>(() =>
    initialSnapshot?.dynamicData || {
      guests: data?.guests,
      videos: data?.videos,
      reports: data?.reports,
      agenda: data?.agenda,
      // 合作伙伴改为只依赖配置接口，避免新页面继续兜底旧 CMS 数据导致问题被掩盖。
      partners: { cooperateCategoryList: [] },
      aiForGood: createEmptyAiForGoodResp(),
      exhibition: createEmptyConferenceExhibitionResp(),
      news: createEmptyConferenceNewsResp(),
    }
  );
  const [momentsData, setMomentsData] = useState<
    ConferenceMomentListResp | undefined
  >(
    initialSnapshot?.momentsData ||
      (data?.moments ? normalizeConferenceMoments(data.moments) : undefined)
  );
  const [themeData, setThemeData] = useState<ConferenceThemeItem | undefined>(
    initialSnapshot?.themeData
  );
  const [highlightsData, setHighlightsData] = useState<
    ConferenceHighlightItem[] | undefined
  >(initialSnapshot?.highlightsData || data?.highlights);
  const initialBrandsData = normalizeBrandContent(data?.brands);
  const [brandsData, setBrandsData] =
    useState<ConferenceBrandItem[]>(
      initialSnapshot
        ? normalizeBrandContent(initialSnapshot.brandsData)
        : initialBrandsData
    );
  const [brandsLoaded, setBrandsLoaded] = useState(
    initialSnapshot?.brandsLoaded === true ||
      data?.conferenceBrandsLoaded === true ||
      initialBrandsData.length > 0
  );
  const [showChatFloatingEntry, setShowChatFloatingEntry] = useState(false);
  const bannerSectionRef = useRef<HTMLDivElement>(null);
  const signupResumeTriggeredRef = useRef(false);
  const chatResumeTriggeredRef = useRef(false);
  // 站点由入口或域名决定，不能用页面语言切换状态决定；中文域名始终请求中文接口。
  const resolvedSite: ConferenceSite = site || (isExternalDomain ? 'en' : 'zh');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const loadMomentsByYear = useCallback(
    async (year: number) =>
      fetchConferenceMoments({
        conferenceYear: year,
        site: resolvedSite,
      }),
    [resolvedSite]
  );

  useEffect(() => {
    let disposed = false;

    const loadInitialDynamicData = async () => {
      if (!initialSnapshot) {
        setIndependentSectionsReady(false);
        setIndependentSectionStates({
          guests: { availableYears: [] },
          videos: { availableYears: [] },
          agenda: { availableYears: [] },
          aiForGood: { availableYears: [] },
          exhibition: { availableYears: [] },
          moments: { availableYears: [] },
          news: { availableYears: [] },
        });
      }
      // 客户端始终复查品牌营销数据；与服务端首屏结果一致时不触发状态更新。
      void fetchConferenceBrands(resolvedSite).then((brands) => {
        if (disposed) return;
        if (brands !== undefined) {
          const nextBrands = normalizeBrandContent(brands);
          setBrandsData((current) =>
            hasSameBrandContent(current, nextBrands) ? current : nextBrands
          );
        }
        setBrandsLoaded(true);
      });
      const nextSnapshot = await fetchConferenceInitialSnapshot(resolvedSite);

      if (!disposed) {
        const mergedSnapshot = initialSnapshot
          ? mergeConferenceInitialSnapshot(initialSnapshot, nextSnapshot)
          : nextSnapshot;
        if (mergedSnapshot === initialSnapshot) {
          return;
        }
        setYearOptions((current) =>
          isSameConferenceData(current, mergedSnapshot.yearOptions)
            ? current
            : mergedSnapshot.yearOptions
        );
        setSelectedYear((current) =>
          current === mergedSnapshot.selectedYear
            ? current
            : mergedSnapshot.selectedYear
        );
        setDynamicData((current) =>
          isSameConferenceData(current, mergedSnapshot.dynamicData)
            ? current
            : mergedSnapshot.dynamicData
        );
        setIndependentSectionStates((current) =>
          isSameConferenceData(
            current,
            mergedSnapshot.independentSectionStates
          )
            ? current
            : mergedSnapshot.independentSectionStates
        );
        setIndependentSectionsReady((current) =>
          current === mergedSnapshot.independentSectionsReady
            ? current
            : mergedSnapshot.independentSectionsReady
        );
        setThemeData((current) =>
          isSameConferenceData(current, mergedSnapshot.themeData)
            ? current
            : mergedSnapshot.themeData
        );
        setHighlightsData((current) =>
          isSameConferenceData(current, mergedSnapshot.highlightsData)
            ? current
            : mergedSnapshot.highlightsData
        );
        setMomentsData((current) =>
          isSameConferenceData(current, mergedSnapshot.momentsData)
            ? current
            : mergedSnapshot.momentsData
        );
      }
    };

    loadInitialDynamicData();

    return () => {
      disposed = true;
    };
  }, [initialSnapshot, resolvedSite]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (chatResumeTriggeredRef.current) {
      return;
    }

    const searchParams = new URL(window.location.href).searchParams;
    const sFrom = searchParams.get('s_from');
    if (sFrom !== 'coral_login' && sFrom !== 'coral_realname') {
      return;
    }

    const pendingAction = loadConferenceChatPendingAction();
    if (!pendingAction) {
      return;
    }

    chatResumeTriggeredRef.current = true;
    let cancelled = false;

    // 未登录点击会聊时，登录页只能回到 S 大会首页；首页拿到登录态后再续调 auth/info。
    // 有权限才进入对应语言会聊页，无权限则展示后端返回提示并停留在首页。
    const resumeConferenceChat = async (retryCount = 0) => {
      const result = await requestConferenceChatEntry({
        authApi: pendingAction.authApi,
        site: pendingAction.site || resolvedSite,
        targetSearchParams: pendingAction.targetSearchParams,
      });
      if (cancelled) {
        return;
      }
      if (result.kind === 'ok') {
        clearConferenceChatPendingAction();
        window.location.href = result.url;
        return;
      }
      if (result.kind === 'login' && retryCount < 1) {
        window.setTimeout(() => {
          void resumeConferenceChat(retryCount + 1);
        }, 300);
        return;
      }

      clearConferenceChatPendingAction();
      if (result.kind === 'denied') {
        MessagePlugin.warning({
          zIndex: 1000000,
          content: result.message,
        });
      }
    };

    void resumeConferenceChat();

    return () => {
      cancelled = true;
    };
  }, [resolvedSite]);

  useEffect(() => {
    if (resolvedSite === 'en' || typeof window === 'undefined') {
      return;
    }
    if (signupResumeTriggeredRef.current) {
      return;
    }

    const searchParams = new URL(window.location.href).searchParams;
    const sFrom = searchParams.get('s_from');
    if (sFrom !== 'coral_login' && sFrom !== 'coral_realname') {
      return;
    }

    const pendingAction = loadConferenceSignupPendingAction();
    if (!pendingAction?.surveyUrl) {
      clearConferenceSignupPendingAction();
      return;
    }

    signupResumeTriggeredRef.current = true;
    let cancelled = false;

    // 登录回跳后不展示中间页，直接续调 portal 拿最终问卷地址；若登录态尚未落稳，短暂重试一次。
    const resumeConferenceSignup = async (retryCount = 0) => {
      const result = await requestConferenceSignupRedirect({
        surveyUrl: pendingAction.surveyUrl,
      });
      if (cancelled) {
        return;
      }
      if (result.kind === 'ok') {
        clearConferenceSignupPendingAction();
        window.location.href = result.url;
        return;
      }
      if (result.kind === 'login' && retryCount < 1) {
        window.setTimeout(() => {
          void resumeConferenceSignup(retryCount + 1);
        }, 300);
        return;
      }

      clearConferenceSignupPendingAction();
      MessagePlugin.error({
        zIndex: 1000000,
        content:
          result.kind === 'login'
            ? '登录态未生效，请重新点击报名'
            : result.message,
      });
    };

    void resumeConferenceSignup();

    return () => {
      cancelled = true;
    };
  }, [resolvedSite]);

  useEffect(() => {
    if (isMobile || typeof window === 'undefined') {
      setShowChatFloatingEntry(false);
      return;
    }

    const updateChatEntryVisible = () => {
      const bannerSection = bannerSectionRef.current;
      if (!bannerSection) {
        return;
      }

      const rect = bannerSection.getBoundingClientRect();
      const isBannerShorterThanViewport = rect.height < window.innerHeight;
      const nextVisible =
        isBannerShorterThanViewport || rect.bottom < window.innerHeight - 1;

      setShowChatFloatingEntry((prev) =>
        prev === nextVisible ? prev : nextVisible
      );
    };

    let ticking = false;
    const handleScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        updateChatEntryVisible();
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateChatEntryVisible);
    updateChatEntryVisible();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateChatEntryVisible);
    };
  }, [isMobile]);

  // 主题配置字段映射：兼容后端可能返回 snake_case、camelCase 或 PascalCase
  const banner = themeData
    ? {
        titleCn:
          (themeData as any).title_cn ||
          (themeData as any).titleCn ||
          (themeData as any).TitleCN ||
          '',
        titleEn:
          (themeData as any).title_en ||
          (themeData as any).titleEn ||
          (themeData as any).TitleEN ||
          '',
        subtitleCn:
          (themeData as any).subtitle_cn ||
          (themeData as any).subtitleCn ||
          (themeData as any).SubtitleCN ||
          '',
        subtitleEn:
          (themeData as any).subtitle_en ||
          (themeData as any).subtitleEn ||
          (themeData as any).SubtitleEN ||
          '',
        startDate:
          (themeData as any).start_date ||
          (themeData as any).startDate ||
          (themeData as any).StartDate ||
          '',
        addressCn:
          (themeData as any).address_cn ||
          (themeData as any).addressCn ||
          (themeData as any).AddressCN ||
          '',
        addressEn:
          (themeData as any).address_en ||
          (themeData as any).addressEn ||
          (themeData as any).AddressEN ||
          '',
        primaryBtnCn:
          (themeData as any).primary_btn_cn ||
          (themeData as any).primaryBtnCn ||
          (themeData as any).PrimaryBtnCN ||
          '',
        primaryBtnEn:
          (themeData as any).primary_btn_en ||
          (themeData as any).primaryBtnEn ||
          (themeData as any).PrimaryBtnEN ||
          '',
        primaryBtnUrl:
          (themeData as any).primary_btn_url ||
          (themeData as any).primaryBtnUrl ||
          (themeData as any).PrimaryBtnURL ||
          '',
        primaryBtnOverseaUrl:
          (themeData as any).primary_btn_oversea_url ||
          (themeData as any).primaryBtnOverseaUrl ||
          (themeData as any).PrimaryBtnOverseaURL ||
          '',
        secondaryBtnCn:
          (themeData as any).secondary_btn_cn ||
          (themeData as any).secondaryBtnCn ||
          (themeData as any).SecondaryBtnCN ||
          '',
        secondaryBtnEn:
          (themeData as any).secondary_btn_en ||
          (themeData as any).secondaryBtnEn ||
          (themeData as any).SecondaryBtnEN ||
          '',
        secondaryBtnUrl:
          (themeData as any).secondary_btn_url ||
          (themeData as any).secondaryBtnUrl ||
          (themeData as any).SecondaryBtnURL ||
          '',
        secondaryBtnOverseaUrl:
          (themeData as any).secondary_btn_oversea_url ||
          (themeData as any).secondaryBtnOverseaUrl ||
          (themeData as any).SecondaryBtnOverseaURL ||
          '',
        coverImage:
          (themeData as any).cover_image ||
          (themeData as any).coverImage ||
          (themeData as any).CoverImage ||
          '',
      }
    : undefined;

  const handleYearChange = async (year: number) => {
    setSelectedYear(year);
    // 顶部年份切换只刷新公共 Banner 联动的精彩瞬间；
    // 嘉宾、议程、视频、AI 向善、大会展区、大会资讯均由模块内部独立切年。
    const nextMoments = await loadMomentsByYear(year);
    if (nextMoments) {
      setMomentsData(nextMoments);
    }
  };

  const aiForGoodList = getConferenceAiForGoodList(dynamicData.aiForGood);
  const aiForGoodSectionData = useMemo(
    () => ({
      ...(dynamicData.aiForGood || createEmptyAiForGoodResp()),
      list: aiForGoodList,
    }),
    [aiForGoodList, dynamicData.aiForGood]
  );
  const hasAiForGoodData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.aiForGood);
  const hasExhibitionData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.exhibition);
  const hasNewsData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.news);
  const hasGuestsSectionData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.guests);
  const hasAgendaSectionData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.agenda);
  const hasVideosSectionData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.videos);
  const hasBrandContent = brandsData.length > 0;
  const enableServerVideoPoster =
    data?.conferenceEnablePcBannerSsr === true;
  const enableServerMobileImage =
    data?.conferenceEnableMobileBannerSsr === true;
  const hasHighlightsData = hasConferenceListData(highlightsData);
  const hasReportsData = hasConferenceListData(dynamicData.reports?.list);
  const hasMomentsData =
    independentSectionsReady &&
    isConferenceSectionYearStateVisible(independentSectionStates.moments);
  const publicBannerSections: PublicBannerTabId[] = [
    hasGuestsSectionData ? 'guests' : null,
    hasAgendaSectionData ? 'agenda' : null,
    hasAiForGoodData ? 'ai-for-good' : null,
    hasExhibitionData ? 'exhibition' : null,
    hasVideosSectionData ? 'videos' : null,
    hasMomentsData ? 'moments' : null,
  ].filter(Boolean) as PublicBannerTabId[];

  return (
    <MeetingConfigProvider
      mainConfig={mainConfig}
      isExternalDomain={isExternalDomain}
    >
      {isMobile ? <ConferenceMobileRemController /> : null}
      <div
        className="min-w-[1200px] mobile:min-w-full bg-[#EDF3FF] flex flex-col"
        style={{
          // 大会页统一使用系统字体栈：优先命中各系统 UI 字体，再回退到常见中文无衬线字体。
          fontFamily:
            'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", "Noto Sans CJK SC", "Source Han Sans SC", Arial, sans-serif',
        }}
      >
        {/* 导航（S 大会页移动端不展示） */}
        {isExternalDomain ? (
          <SSVNavbar
            className={styles.conferenceNav}
            userMenuVariant="conference-signup"
          />
        ) : (
          // S 大会中文站退出登录保持在当前页，避免跳回登录页
          <PCNavbar type="white" onLogout={handleLogoutStay} />
        )}
        {isMobile || showChatFloatingEntry ? (
          <ConferenceChatFloatingEntry site={resolvedSite} />
        ) : null}

        {/* 内容 */}
        <div className="w-full relative">
          {/* 主题内容固定展示在前景，品牌营销有值时作为背景轮播；无品牌营销时回退主题背景图。 */}
          <div ref={bannerSectionRef}>
            <BannerSection
              data={banner}
              brands={hasBrandContent ? brandsData : []}
              brandsLoaded={brandsLoaded}
              enableServerVideoPoster={enableServerVideoPoster}
              enableServerMobileImage={enableServerMobileImage}
              site={resolvedSite}
              languageSwitch={<ConferenceLanguageSwitch />}
            />
          </div>

          <div
            className={`${styles.anchoredContent} ${
              hasHighlightsData ? styles.anchoredContentWithHighlights : ''
            } ${resolvedSite === 'en' ? styles.anchoredContentOverseas : ''}`}
          >
            {/* 大会亮点 */}
            {hasHighlightsData ? (
              <HighlightsSection
                data={highlightsData}
                title={getTitle('highlights', locale)}
                site={site}
              />
            ) : null}

            {/* 公共 Banner - 放在嘉宾上方 */}
            <PublicBanner
              data={data?.publicBanner}
              selectedYear={selectedYear}
              years={yearOptions}
              onYearChange={handleYearChange}
              availableSections={publicBannerSections}
            />

            {/* 大会嘉宾 */}
            {hasGuestsSectionData ? (
              <GuestsSection
                data={dynamicData.guests}
                title={getTitle('guests', locale)}
                site={resolvedSite}
                years={independentSectionStates.guests.availableYears}
              />
            ) : null}

            {/* 大会议程 */}
            {hasAgendaSectionData ? (
              <AgendaSection
                data={dynamicData.agenda}
                title={getTitle('agenda', locale)}
                site={resolvedSite}
                years={independentSectionStates.agenda.availableYears}
              />
            ) : null}

            {/* AI 向善 */}
            {hasAiForGoodData ? (
              <AiForGoodSection
                data={aiForGoodSectionData}
                title={getTitle('aiForGood', locale)}
                years={independentSectionStates.aiForGood.availableYears}
                site={resolvedSite}
              />
            ) : null}

            {/* 大会展区 */}
            {hasExhibitionData ? (
              <ExhibitionSection
                data={dynamicData.exhibition}
                title={getTitle('exhibition', locale)}
                years={independentSectionStates.exhibition.availableYears}
                site={resolvedSite}
              />
            ) : null}

            {/* 精彩视频 */}
            {hasVideosSectionData ? (
              <VideosSection
                data={dynamicData.videos}
                title={getTitle('videos', locale)}
                site={resolvedSite}
                years={independentSectionStates.videos.availableYears}
              />
            ) : null}

            {/* 精彩瞬间 */}
            {hasMomentsData ? (
              <MomentsSection
                data={momentsData}
                title={getTitle('moments', locale)}
                site={resolvedSite}
                years={independentSectionStates.moments.availableYears}
              />
            ) : null}

            {/* 大会资讯 */}
            {hasNewsData ? (
              <NewsSection
                data={dynamicData.news}
                title={getTitle('news', locale)}
                site={resolvedSite}
                years={independentSectionStates.news.availableYears}
              />
            ) : null}

            {/* 行业报告 */}
            {hasReportsData ? (
              <ReportsSection
                data={dynamicData.reports}
                title={getTitle('reports', locale)}
                site={resolvedSite}
              />
            ) : null}

            {/* 合作伙伴 */}
            <PartnersSection
              data={dynamicData.partners}
              title={getTitle('partners', locale)}
              site={resolvedSite}
            />
          </div>
        </div>

        {/* 底部 */}
        <SSVFooter
          bgColor="#0A64E4"
          bgImage={getCosImageUrl('web-ui/footer/images/footer-blue-bg.png')}
          bgImage_m={getCosImageUrl('web-ui/footer/images/footer-blue-mobile-bg.svg')}
        />
      </div>
    </MeetingConfigProvider>
  );
}
